// =============================================================================
// DRIP EMAIL TEMPLATES
// Educational nurture sequence to bring leads back into the funnel
// =============================================================================

import { logger } from '@/utils/logger'
import { COMPANY } from '../config/dripCampaign'

const log = logger.module('DripEmailTemplates')

// Type definitions
interface LeadData {
  firstName?: string
  calendlyUrl?: string
  projectType?: string
  [key: string]: unknown
}

interface EmailTemplate {
  subject: string
  html: string
  text: string
}

// Get the next month + 8 weeks for scarcity email
function getBookingMonth(): string {
  const date = new Date()
  date.setDate(date.getDate() + 56) // 8 weeks out
  return date.toLocaleDateString('en-US', { month: 'long' })
}

// Base email wrapper
function wrapEmail(content: string): string {
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: Georgia, 'Times New Roman', serif; background-color: #f9f6f1; color: #2d2d2d;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f9f6f1; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 4px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.08);">
          ${content}
          <!-- Footer -->
          <tr>
            <td style="padding: 32px; border-top: 1px solid #e8e4de; text-align: center;">
              <p style="margin: 0 0 8px; color: #8b7355; font-size: 14px; font-style: italic;">
                ${COMPANY.tagline}
              </p>
              <p style="margin: 0; color: #999; font-size: 12px;">
                ${COMPANY.name} • ${COMPANY.phone}
              </p>
              <p style="margin: 8px 0 0; color: #999; font-size: 11px;">
                <a href="{{unsubscribeUrl}}" style="color: #999;">Unsubscribe</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
}

// =============================================================================
// EMAIL TEMPLATES
// =============================================================================

export const DRIP_TEMPLATES: Record<string, (lead: LeadData) => EmailTemplate> = {
  
  // =========================================================================
  // EMAIL 1: Safety & Quality (Day 3)
  // =========================================================================
  safety_quality: (lead) => ({
    subject: "What IKEA won't tell you about bunk beds",
    html: wrapEmail(`
      <tr>
        <td style="padding: 40px 40px 24px;">
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            Hi ${lead.firstName || 'there'},
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            I wanted to share something most furniture stores won't tell you.
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            <strong style="color: #8b7355;">Thousands of bunk bed injuries go unreported every year.</strong> The Consumer Product Safety Commission only captures a fraction of the falls, collapses, and structural failures that happen in homes across America.
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            Most of these injuries share one thing in common: <strong>mass-produced furniture that was never designed to be truly safe.</strong>
          </p>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 24px;">
          <div style="background: #fdf8f3; border-left: 4px solid var(--color-gold); padding: 20px; margin: 0 0 20px;">
            <p style="margin: 0 0 12px; font-size: 14px; font-weight: bold; color: #8b7355;">The Problem with "Wayfair-Style" Bunks:</p>
            <p style="margin: 0; font-size: 14px; line-height: 1.6; color: #5a5a5a;">
              ❌ Particle board – compressed sawdust that weakens over time<br>
              ❌ Cam-lock assembly – connections that loosen with every climb<br>
              ❌ Freestanding design – nothing attaching it to your wall studs<br>
              ❌ 5-year lifespan – if you're lucky
            </p>
          </div>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 24px;">
          <div style="background: #f0f7f4; border-left: 4px solid #4a7c59; padding: 20px;">
            <p style="margin: 0 0 12px; font-size: 14px; font-weight: bold; color: #4a7c59;">What We Build Instead:</p>
            <p style="margin: 0; font-size: 14px; line-height: 1.6; color: #5a5a5a;">
              ✅ Hardwood plywood base – never particle board<br>
              ✅ 100% real hardwood upgrades – your choice of species<br>
              ✅ Wall-mounted to studs – eliminates racking completely<br>
              ✅ Matching trim packages – integrates with your home<br>
              ✅ Heirloom quality – built to outlast your kids' childhoods
            </p>
          </div>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 32px;">
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            Would you put your children 5 feet in the air on compressed sawdust? Or would you rather have them sleep on solid hardwood, anchored to your wall studs?
          </p>
          <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.7;">
            Ready to see what custom looks like?
          </p>
          <a href="${lead.calendlyUrl}" style="display: inline-block; background: linear-gradient(135deg, var(--color-gold) 0%, #8b7635 100%); color: #0a0a0a; padding: 14px 28px; border-radius: 4px; text-decoration: none; font-weight: 600; font-size: 15px;">
            📅 Book a Free 15-Minute Consultation
          </a>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 32px;">
          <p style="margin: 0; font-size: 16px; line-height: 1.7;">
            – Jason
          </p>
        </td>
      </tr>
    `),
    text: `Hi ${lead.firstName || 'there'},

I wanted to share something most furniture stores won't tell you.

Thousands of bunk bed injuries go unreported every year. The Consumer Product Safety Commission only captures a fraction of the falls, collapses, and structural failures that happen in homes across America.

Most of these injuries share one thing in common: mass-produced furniture that was never designed to be truly safe.

The Problem with "Wayfair-Style" Bunks:
❌ Particle board – compressed sawdust that weakens over time
❌ Cam-lock assembly – connections that loosen with every climb
❌ Freestanding design – nothing attaching it to your wall studs
❌ 5-year lifespan – if you're lucky

What We Build Instead:
✅ Hardwood plywood base – never particle board
✅ 100% real hardwood upgrades – your choice of species
✅ Wall-mounted to studs – eliminates racking completely
✅ Matching trim packages – integrates with your home
✅ Heirloom quality – built to outlast your kids' childhoods

Ready to see what custom looks like?
${lead.calendlyUrl}

– Jason
${COMPANY.name}`
  }),

  // =========================================================================
  // EMAIL 2: Social Proof (Day 5)
  // =========================================================================
  social_proof: (lead) => ({
    subject: "How the Hendersons transformed their vacation rental",
    html: wrapEmail(`
      <tr>
        <td style="padding: 40px 40px 24px;">
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            Hi ${lead.firstName || 'there'},
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            I wanted to share a quick story.
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            Last year, the Henderson family came to us with a problem: their Destin beach house had two guest bedrooms that could only sleep 4 people. With rental income tied to headcount, they were leaving money on the table.
          </p>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 24px;">
          <div style="background: #fdf8f3; border-radius: 8px; padding: 24px;">
            <p style="margin: 0 0 12px; font-size: 14px; font-weight: bold; color: #8b7355;">Our Solution:</p>
            <p style="margin: 0 0 16px; font-size: 14px; line-height: 1.6; color: #5a5a5a;">
              Custom triple-stacked bunks with integrated reading lights, USB charging, and privacy curtains – designed to match their existing coastal trim.
            </p>
            <p style="margin: 0 0 12px; font-size: 14px; font-weight: bold; color: #4a7c59;">The Results:</p>
            <p style="margin: 0; font-size: 14px; line-height: 1.6; color: #5a5a5a;">
              ✅ Sleeps 12 instead of 4<br>
              ✅ Rental income up 40%<br>
              ✅ Guests specifically mention the bunks in 5-star reviews<br>
              ✅ Zero structural issues after 2 hurricane seasons
            </p>
          </div>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 32px;">
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            The Hendersons didn't buy furniture. <strong style="color: #8b7355;">They made an investment that pays them back every month.</strong>
          </p>
          <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.7;">
            What could custom bunks do for your space?
          </p>
          <a href="${lead.calendlyUrl}" style="display: inline-block; background: linear-gradient(135deg, var(--color-gold) 0%, #8b7635 100%); color: #0a0a0a; padding: 14px 28px; border-radius: 4px; text-decoration: none; font-weight: 600; font-size: 15px;">
            📅 Let's Talk (Free 15-Min Call)
          </a>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 32px;">
          <p style="margin: 0; font-size: 16px; line-height: 1.7;">
            – Jason
          </p>
        </td>
      </tr>
    `),
    text: `Hi ${lead.firstName || 'there'},

I wanted to share a quick story.

Last year, the Henderson family came to us with a problem: their Destin beach house had two guest bedrooms that could only sleep 4 people. With rental income tied to headcount, they were leaving money on the table.

Our Solution: Custom triple-stacked bunks with integrated reading lights, USB charging, and privacy curtains – designed to match their existing coastal trim.

The Results:
✅ Sleeps 12 instead of 4
✅ Rental income up 40%
✅ Guests specifically mention the bunks in 5-star reviews
✅ Zero structural issues after 2 hurricane seasons

The Hendersons didn't buy furniture. They made an investment that pays them back every month.

What could custom bunks do for your space?
${lead.calendlyUrl}

– Jason
${COMPANY.name}`
  }),

  // =========================================================================
  // EMAIL 3: Objection Handler (Day 8)
  // =========================================================================
  objection_handler: (lead) => ({
    subject: "Is a custom bunk bed really worth it?",
    html: wrapEmail(`
      <tr>
        <td style="padding: 40px 40px 24px;">
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            Hi ${lead.firstName || 'there'},
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            I get it. $5,000-$10,000 for a bunk bed sounds like a lot.
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            So let me break down what you're actually comparing:
          </p>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 24px;">
          <table width="100%" cellpadding="12" cellspacing="0" style="border: 1px solid #e8e4de; border-radius: 8px; font-size: 13px;">
            <tr style="background: #fdf8f3;">
              <td style="font-weight: bold; border-bottom: 1px solid #e8e4de;"></td>
              <td style="font-weight: bold; border-bottom: 1px solid #e8e4de; color: #999;">Big Box Store</td>
              <td style="font-weight: bold; border-bottom: 1px solid #e8e4de; color: #8b7355;">Bliss Custom</td>
            </tr>
            <tr>
              <td style="border-bottom: 1px solid #e8e4de;"><strong>Materials</strong></td>
              <td style="border-bottom: 1px solid #e8e4de; color: #999;">Particle board, MDF</td>
              <td style="border-bottom: 1px solid #e8e4de; color: #4a7c59;">Hardwood plywood + solid hardwood</td>
            </tr>
            <tr>
              <td style="border-bottom: 1px solid #e8e4de;"><strong>Assembly</strong></td>
              <td style="border-bottom: 1px solid #e8e4de; color: #999;">DIY, 4 hours, wobbly</td>
              <td style="border-bottom: 1px solid #e8e4de; color: #4a7c59;">Pro install, wall-anchored</td>
            </tr>
            <tr>
              <td style="border-bottom: 1px solid #e8e4de;"><strong>Lifespan</strong></td>
              <td style="border-bottom: 1px solid #e8e4de; color: #999;">3-5 years</td>
              <td style="border-bottom: 1px solid #e8e4de; color: #4a7c59;">20+ years (heirloom)</td>
            </tr>
            <tr>
              <td style="border-bottom: 1px solid #e8e4de;"><strong>Safety</strong></td>
              <td style="border-bottom: 1px solid #e8e4de; color: #999;">Freestanding, racking risk</td>
              <td style="border-bottom: 1px solid #e8e4de; color: #4a7c59;">Stud-mounted, zero wobble</td>
            </tr>
            <tr>
              <td><strong>Resale</strong></td>
              <td style="color: #999;">$0 (landfill)</td>
              <td style="color: #4a7c59;">Adds home value</td>
            </tr>
          </table>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 24px;">
          <div style="background: #f0f7f4; border-left: 4px solid #4a7c59; padding: 20px;">
            <p style="margin: 0 0 8px; font-size: 14px; font-weight: bold; color: #4a7c59;">The Real Math:</p>
            <p style="margin: 0; font-size: 14px; line-height: 1.6; color: #5a5a5a;">
              • $500 IKEA bunk ÷ 4 years = <strong>$125/year</strong> (then it's trash)<br>
              • $6,000 Bliss bunk ÷ 25 years = <strong>$240/year</strong> (then it's an heirloom)
            </p>
            <p style="margin: 12px 0 0; font-size: 14px; color: #4a7c59; font-weight: bold;">
              For $115 more per year, you get safety, beauty, and furniture your grandkids will use.
            </p>
          </div>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 32px;">
          <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.7;">
            That's not expensive. <strong style="color: #8b7355;">That's smart.</strong>
          </p>
          <a href="${lead.calendlyUrl}" style="display: inline-block; background: linear-gradient(135deg, var(--color-gold) 0%, #8b7635 100%); color: #0a0a0a; padding: 14px 28px; border-radius: 4px; text-decoration: none; font-weight: 600; font-size: 15px;">
            📅 Book Your Free Consultation
          </a>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 32px;">
          <p style="margin: 0; font-size: 16px; line-height: 1.7;">
            – Jason
          </p>
        </td>
      </tr>
    `),
    text: `Hi ${lead.firstName || 'there'},

I get it. $5,000-$10,000 for a bunk bed sounds like a lot.

So let me break down what you're actually comparing:

                    Big Box Store         Bliss Custom
Materials           Particle board        Hardwood plywood + solid hardwood
Assembly            DIY, 4 hours, wobbly  Pro install, wall-anchored
Lifespan            3-5 years             20+ years (heirloom)
Safety              Freestanding          Stud-mounted, zero wobble
Resale              $0 (landfill)         Adds home value

The Real Math:
• $500 IKEA bunk ÷ 4 years = $125/year (then it's trash)
• $6,000 Bliss bunk ÷ 25 years = $240/year (then it's an heirloom)

For $115 more per year, you get safety, beauty, and furniture your grandkids will use.

That's not expensive. That's smart.

${lead.calendlyUrl}

– Jason
${COMPANY.name}`
  }),

  // =========================================================================
  // EMAIL 4: Scarcity (Day 12)
  // =========================================================================
  scarcity: (lead) => ({
    subject: "Our build calendar is filling up",
    html: wrapEmail(`
      <tr>
        <td style="padding: 40px 40px 24px;">
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            Hi ${lead.firstName || 'there'},
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            Quick update: we're currently booking into <strong style="color: #8b7355;">${getBookingMonth()}</strong>.
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            I know that might seem like plenty of time, but here's what happens every year:
          </p>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 24px;">
          <div style="background: #fdf8f3; border-radius: 8px; padding: 24px;">
            <p style="margin: 0; font-size: 14px; line-height: 1.8; color: #5a5a5a;">
              <strong>February:</strong> "We'll wait until spring"<br>
              <strong>April:</strong> "Let's do this before summer!"<br>
              <strong>May:</strong> "Wait, you're booked through August?!"
            </p>
          </div>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 32px;">
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            If you have a deadline in mind – vacation rental season, a birthday, the holidays – <strong>now's the time to lock in your spot.</strong>
          </p>
          <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.7;">
            We hold your place with a 50% deposit. No design work starts until we've nailed every detail together.
          </p>
          <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.7;">
            Just wanted to give you a heads up before the rush.
          </p>
          <a href="${lead.calendlyUrl}" style="display: inline-block; background: linear-gradient(135deg, var(--color-gold) 0%, #8b7635 100%); color: #0a0a0a; padding: 14px 28px; border-radius: 4px; text-decoration: none; font-weight: 600; font-size: 15px;">
            📅 Secure Your Spot
          </a>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 32px;">
          <p style="margin: 0; font-size: 16px; line-height: 1.7;">
            – Jason
          </p>
        </td>
      </tr>
    `),
    text: `Hi ${lead.firstName || 'there'},

Quick update: we're currently booking into ${getBookingMonth()}.

I know that might seem like plenty of time, but here's what happens every year:

February: "We'll wait until spring"
April: "Let's do this before summer!"
May: "Wait, you're booked through August?!"

If you have a deadline in mind – vacation rental season, a birthday, the holidays – now's the time to lock in your spot.

We hold your place with a 50% deposit. No design work starts until we've nailed every detail together.

Just wanted to give you a heads up before the rush.

${lead.calendlyUrl}

– Jason
${COMPANY.name}`
  }),

  // =========================================================================
  // EMAIL 5: Value Stack (Day 17)
  // =========================================================================
  value_stack: (lead) => ({
    subject: "What's actually included in your quote",
    html: wrapEmail(`
      <tr>
        <td style="padding: 40px 40px 24px;">
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            Hi ${lead.firstName || 'there'},
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            I realized I may not have fully explained what you get when you work with us. Here's the full picture:
          </p>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 24px;">
          <div style="background: #f0f7f4; border-radius: 8px; padding: 24px;">
            <p style="margin: 0 0 12px; font-size: 14px; font-weight: bold; color: #4a7c59;">Included in Every Bliss Project:</p>
            <p style="margin: 0; font-size: 14px; line-height: 1.8; color: #5a5a5a;">
              ✅ 30-minute Zoom design consultation<br>
              ✅ Custom CAD drawings of your exact space<br>
              ✅ Material selection (wood type, stain, finish)<br>
              ✅ Trim matching to your home's architecture<br>
              ✅ Professional fabrication (6-8 weeks)<br>
              ✅ White-glove delivery<br>
              ✅ Pro installation with wall-stud anchoring<br>
              ✅ Touch-up kit for future scratches<br>
              ✅ Lifetime structural warranty
            </p>
          </div>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 24px;">
          <div style="background: #fdf8f3; border-radius: 8px; padding: 24px;">
            <p style="margin: 0 0 12px; font-size: 14px; font-weight: bold; color: #999;">Not Included:</p>
            <p style="margin: 0; font-size: 14px; line-height: 1.8; color: #999;">
              ❌ Hidden fees<br>
              ❌ Assembly headaches<br>
              ❌ Wobbly beds<br>
              ❌ Regret
            </p>
          </div>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 32px;">
          <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.7;">
            The quote I sent you covers everything from design to installed-and-done.
          </p>
          <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.7;">
            Questions? Just hit reply.
          </p>
          <a href="${lead.calendlyUrl}" style="display: inline-block; background: linear-gradient(135deg, var(--color-gold) 0%, #8b7635 100%); color: #0a0a0a; padding: 14px 28px; border-radius: 4px; text-decoration: none; font-weight: 600; font-size: 15px;">
            📅 Or Book a Quick Call
          </a>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 32px;">
          <p style="margin: 0; font-size: 16px; line-height: 1.7;">
            – Jason
          </p>
        </td>
      </tr>
    `),
    text: `Hi ${lead.firstName || 'there'},

I realized I may not have fully explained what you get when you work with us. Here's the full picture:

Included in Every Bliss Project:
✅ 30-minute Zoom design consultation
✅ Custom CAD drawings of your exact space
✅ Material selection (wood type, stain, finish)
✅ Trim matching to your home's architecture
✅ Professional fabrication (6-8 weeks)
✅ White-glove delivery
✅ Pro installation with wall-stud anchoring
✅ Touch-up kit for future scratches
✅ Lifetime structural warranty

Not Included:
❌ Hidden fees
❌ Assembly headaches
❌ Wobbly beds
❌ Regret

The quote I sent you covers everything from design to installed-and-done.

Questions? Just hit reply.

${lead.calendlyUrl}

– Jason
${COMPANY.name}`
  }),

  // =========================================================================
  // EMAIL 6: Personal Touch (Day 24)
  // =========================================================================
  personal_touch: (lead) => ({
    subject: "Quick question about your project",
    html: wrapEmail(`
      <tr>
        <td style="padding: 40px 40px 32px;">
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            Hi ${lead.firstName || 'there'},
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            I was reviewing my notes from your quote and wanted to check in.
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            Are you still thinking about custom bunks${lead.projectType ? ` for your ${lead.projectType}` : ''}?
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            No pitch here – just genuinely curious if:
          </p>
          <ul style="margin: 0 0 20px; padding-left: 24px; font-size: 16px; line-height: 1.7;">
            <li>The timing changed?</li>
            <li>You had questions I didn't answer?</li>
            <li>You went a different direction?</li>
          </ul>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            Either way, I'd love to know. It helps me serve future families better.
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            <strong>Just hit reply</strong> – even a one-liner is great.
          </p>
          <p style="margin: 0; font-size: 16px; line-height: 1.7;">
            – Jason
          </p>
        </td>
      </tr>
    `),
    text: `Hi ${lead.firstName || 'there'},

I was reviewing my notes from your quote and wanted to check in.

Are you still thinking about custom bunks${lead.projectType ? ` for your ${lead.projectType}` : ''}?

No pitch here – just genuinely curious if:
- The timing changed?
- You had questions I didn't answer?
- You went a different direction?

Either way, I'd love to know. It helps me serve future families better.

Just hit reply – even a one-liner is great.

– Jason
${COMPANY.name}`
  }),

  // =========================================================================
  // EMAIL 7: Last Chance - PRICE INCREASE WARNING (Day 30)
  // =========================================================================
  last_chance: (lead) => ({
    subject: "🚨 Price increase Feb 1st - last call for current pricing",
    html: wrapEmail(`
      <tr>
        <td style="padding: 40px 40px 24px;">
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            Hi ${lead.firstName || 'there'},
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            I wanted to give you a heads up before it's too late.
          </p>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 24px;">
          <div style="background: #fef2f2; border: 2px solid #ef4444; border-radius: 8px; padding: 24px;">
            <p style="margin: 0 0 12px; font-size: 18px; font-weight: bold; color: #dc2626;">
              ⚠️ Prices Increase February 1st
            </p>
            <p style="margin: 0; font-size: 14px; line-height: 1.6; color: #5a5a5a;">
              Due to significant increases in costs throughout our supply chain – lumber, hardware, and labor – we're implementing a price adjustment starting <strong>February 1st, 2026</strong>.
            </p>
          </div>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 24px;">
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            <strong style="color: #8b7355;">This is your last chance to lock in current pricing.</strong>
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            If you've been on the fence, now is the time. A 50% deposit before February 1st guarantees your quote at today's prices – even if we don't start fabrication until spring.
          </p>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 24px;">
          <div style="background: #f0f7f4; border-left: 4px solid #4a7c59; padding: 20px;">
            <p style="margin: 0 0 8px; font-size: 14px; font-weight: bold; color: #4a7c59;">Lock In Your Price:</p>
            <p style="margin: 0; font-size: 14px; line-height: 1.6; color: #5a5a5a;">
              ✅ 50% deposit holds current pricing<br>
              ✅ No design commitment until you're ready<br>
              ✅ Flexible production scheduling<br>
              ✅ Your quote stays valid even if costs rise
            </p>
          </div>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 32px;">
          <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.7;">
            I don't do fake urgency – this is a real cost increase we're passing along. If you're ready, let's make it happen before February.
          </p>
          <a href="${lead.calendlyUrl}" style="display: inline-block; background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); color: #ffffff; padding: 14px 28px; border-radius: 4px; text-decoration: none; font-weight: 600; font-size: 15px;">
            🔒 Lock In Current Pricing
          </a>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 32px;">
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            – Jason
          </p>
          <p style="margin: 0; font-size: 14px; line-height: 1.7; color: #999; font-style: italic;">
            P.S. – If custom bunks aren't in the cards right now, just reply "close it" and I'll stop emailing. No hard feelings.
          </p>
        </td>
      </tr>
    `),
    text: `Hi ${lead.firstName || 'there'},

I wanted to give you a heads up before it's too late.

⚠️ PRICES INCREASE FEBRUARY 1ST

Due to significant increases in costs throughout our supply chain – lumber, hardware, and labor – we're implementing a price adjustment starting February 1st, 2026.

This is your last chance to lock in current pricing.

If you've been on the fence, now is the time. A 50% deposit before February 1st guarantees your quote at today's prices – even if we don't start fabrication until spring.

Lock In Your Price:
✅ 50% deposit holds current pricing
✅ No design commitment until you're ready
✅ Flexible production scheduling
✅ Your quote stays valid even if costs rise

I don't do fake urgency – this is a real cost increase we're passing along. If you're ready, let's make it happen before February.

${lead.calendlyUrl}

– Jason

P.S. – If custom bunks aren't in the cards right now, just reply "close it" and I'll stop emailing. No hard feelings.

${COMPANY.name}`
  }),

  // =========================================================================
  // EMAIL 8: Final 10% Off Last Ditch - 10 HOUR COUNTDOWN
  // Use: Add ?discount=10&expires=<ISO timestamp> to quote approval link
  // Example: /quote-approve/abc123?discount=10&expires=2026-01-13T03:00:00.000Z
  // =========================================================================
  last_ditch_10off: (lead) => {
    // Generate expiration timestamp 10 hours from now
    const expiresAt = new Date(Date.now() + 10 * 60 * 60 * 1000).toISOString()
    // Build the quote approval link with discount params
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://quote-goat.vercel.app'
    const quoteId = lead.quoteId || lead.id || ''
    const discountLink = `${baseUrl}/quote-approve/${quoteId}?discount=10&expires=${encodeURIComponent(expiresAt)}`
    
    return {
    subject: "⏰ 10% off expires in 10 HOURS - final offer",
    html: wrapEmail(`
      <tr>
        <td style="padding: 40px 40px 24px;">
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            Hi ${lead.firstName || 'there'},
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            I'm about to close your project file, but before I do, I wanted to make one final offer.
          </p>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 24px;">
          <div style="background: linear-gradient(135deg, var(--color-charcoal) 0%, #16213e 100%); border: 3px solid #ef4444; border-radius: 12px; padding: 28px; text-align: center;">
            <p style="margin: 0 0 12px; font-size: 14px; font-weight: bold; color: #ef4444; letter-spacing: 2px;">
              ⏰ THIS OFFER EXPIRES IN
            </p>
            <div style="display: inline-block; background: #0f0f23; border-radius: 8px; padding: 16px 32px; margin: 0 0 16px;">
              <span style="font-size: 48px; font-weight: 900; color: #fbbf24; font-family: 'Courier New', monospace; letter-spacing: 4px;">10:00:00</span>
            </div>
            <p style="margin: 0 0 4px; font-size: 11px; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px;">
              Hours : Minutes : Seconds
            </p>
            <div style="margin: 20px 0 0; padding: 16px; background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border-radius: 8px;">
              <p style="margin: 0 0 4px; font-size: 14px; font-weight: bold; color: #92400e;">
                🔥 EXCLUSIVE LAST-CHANCE OFFER
              </p>
              <p style="margin: 0; font-size: 42px; font-weight: 900; color: #c9a227;">
                10% OFF
              </p>
              <p style="margin: 4px 0 0; font-size: 13px; color: #78350f;">
                Your entire order
              </p>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 24px;">
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            I don't normally do this, but I'd rather see your family enjoy our bunks than let your quote expire.
          </p>
          <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.7;">
            <strong style="color: #ef4444;">Reply within the next 10 hours</strong> to lock in this discount. After that, the offer disappears and I'll close your file.
          </p>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 24px;">
          <div style="background: #f0f7f4; border-left: 4px solid #4a7c59; padding: 20px;">
            <p style="margin: 0 0 8px; font-size: 14px; font-weight: bold; color: #4a7c59;">To claim your 10% discount:</p>
            <p style="margin: 0; font-size: 16px; line-height: 1.6; color: #5a5a5a;">
              Simply reply to this email with <strong>"I'm interested"</strong> and I'll send your updated quote with the discount applied.
            </p>
          </div>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 32px;">
          <a href="${discountLink}" style="display: inline-block; background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); color: #ffffff; padding: 16px 32px; border-radius: 8px; text-decoration: none; font-weight: 700; font-size: 16px;">
            ⏰ View Your Discounted Quote
          </a>
          <p style="margin: 16px 0 0; font-size: 12px; color: #999;">
            Click to see your 10% discount applied
          </p>
        </td>
      </tr>
      <tr>
        <td style="padding: 0 40px 32px;">
          <p style="margin: 0; font-size: 16px; line-height: 1.7;">
            – Jason
          </p>
        </td>
      </tr>
    `),
    text: `Hi ${lead.firstName || 'there'},

I'm about to close your project file, but before I do, I wanted to make one final offer.

⏰ THIS OFFER EXPIRES IN 10 HOURS ⏰

🔥 EXCLUSIVE LAST-CHANCE OFFER: 10% OFF your entire order

I don't normally do this, but I'd rather see your family enjoy our bunks than let your quote expire.

Reply within the next 10 hours to lock in this discount. After that, the offer disappears and I'll close your file.

TO CLAIM YOUR 10% DISCOUNT:
Click to view your discounted quote: ${discountLink}

Or simply reply to this email with "I'm interested" and I'll send your updated quote.

– Jason
${COMPANY.name}`
  }
  }
}

export default DRIP_TEMPLATES

